import { parseMoney } from '@betterment/core';
import { useGraphQLQuery } from '@betterment/graphql';
import { useSessionStorage } from '@betterment/js-runtime';
import { Box, Button, FormikField as Field, H1, Input, Select, Text, VStack } from '@betterment/samba-react';
import { WorkflowLayout } from '@betterment/shared-components';
import { Form, Formik, FormikHelpers } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';

import Footer from '../../../../Shared/components-v2/footer/Footer';
import { convertDisplayableErrors } from '../../../../Shared/displayable-errors';
import { ProductCategory } from '../../../data';
import { useCompleteDraftPurposeFlow } from '../../hooks/useCompleteDraftPurposeFlow';
import { target, useMajorPurchaseStep } from '../FlowConfig';
import { MajorPurchasePurposeCreationOptionDocument } from '../graphql/query.majorPurchasePurposeCreationOption.generated';

type FormValues = Yup.InferType<typeof target>;

export const TargetStep: React.FC = () => {
  const navigate = useNavigate();
  const { flowId } = useParams<{ flowId: string }>();

  const { commit, dependencies } = useMajorPurchaseStep('target');
  const { data } = useGraphQLQuery(MajorPurchasePurposeCreationOptionDocument, {
    variables: { id: `major_purchase:${dependencies.nameAndImage.purpose_sub_type}` },
    throwOnError: true,
  });

  // This is a temporary solution until we have a more permanent way to persist the product category
  // through different flows. We want to persist the product category so we can pass it to the AddGoalFlow.
  const { values: productCategory } = useSessionStorage<ProductCategory>('purpose-flow-product-category');

  const { completeFlow, errors: displayableErrors } = useCompleteDraftPurposeFlow({
    draftEnvelopeId: flowId,
    name: dependencies.nameAndImage.name,
    purposeSubType: dependencies.nameAndImage.purpose_sub_type,
    imageType: dependencies.nameAndImage.imageType,
    productCategory,
  });

  if (!data || data.purposeCreationOption.__typename !== 'MajorPurchasePurposeCreationOption') return null;

  const { purposeCreationOption: majorPurchaseOption } = data;

  const initialValues: FormValues = {
    month: majorPurchaseOption.defaultTargetDate.toLocaleString('en-US', { month: 'short' }),
    year: majorPurchaseOption.defaultTargetDate.toLocaleString('en-US', { year: 'numeric' }),
  };

  const months = Array.from({ length: 12 }, (_, i) =>
    new Date(2000, i, 1).toLocaleString(undefined, { month: 'short' })
  );
  const years = Array.from({ length: 51 })
    .map((_, i) => new Date().getFullYear() + i)
    .map(year => ({ label: year.toString(), value: year.toString() }));

  const handleSubmit = async (formValues: FormValues, formikHelpers: FormikHelpers<FormValues>) => {
    commit(formValues);

    const targetDate = new Date(`${formValues.month} 1, ${formValues.year}`);

    await completeFlow({
      targetAmount: formValues.amount ? parseMoney(formValues.amount) : null,
      purposeInput: {
        majorPurchase: { targetDate: targetDate.toISOString() },
      },
    });

    if (displayableErrors.length > 0) {
      formikHelpers.setErrors(convertDisplayableErrors(displayableErrors));
    }
  };

  return (
    <WorkflowLayout pageTitle="Major Purchase Target">
      <WorkflowLayout.Header workflowTitle={null} onClose={() => navigate('/')} onBack={() => navigate(-1)} />
      <WorkflowLayout.Content>
        <VStack css={{ gap: 'space.300' }}>
          <VStack css={{ gap: 'space.200' }}>
            <H1 css={{ textWrap: 'auto' }}>Set your investing target.</H1>
            <Text>Pick a target for how much and when you want your money. You can always change it later.</Text>
          </VStack>
          <Formik
            onSubmit={handleSubmit}
            initialValues={initialValues}
            validationSchema={target}
            validateOnChange={false}
            validateOnBlur={false}
          >
            <Form>
              <VStack css={{ gap: 'space.400' }}>
                <Box css={{ display: 'grid', gap: 'space.200' }}>
                  <Field name="amount">
                    <Field.Label>Target amount (Optional)</Field.Label>
                    <Field.Control>
                      <Input mask="number" leadingSymbol="$" />
                    </Field.Control>
                    <Field.ErrorMessage />
                  </Field>
                  <VStack css={{ gap: 'space.100' }}>
                    <Box
                      css={{
                        gap: 'space.300',
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        alignItems: 'start',
                      }}
                    >
                      <Field name="month">
                        <Field.Label>Target date</Field.Label>
                        <Field.Control>
                          <Select>
                            <Select.Trigger aria-label="Select the target month">
                              <Select.Value />
                            </Select.Trigger>
                            <Select.Content>
                              {months.map(month => (
                                <Select.Item key={month} value={month}>
                                  {month}
                                </Select.Item>
                              ))}
                            </Select.Content>
                          </Select>
                        </Field.Control>
                        <Field.ErrorMessage />
                      </Field>
                      <Field name="year">
                        <Field.Label>
                          <Text css={{ visibility: 'hidden' }}>Target year</Text>
                        </Field.Label>
                        <Field.Control>
                          <Select>
                            <Select.Trigger aria-label="Select the target year">
                              <Select.Value />
                            </Select.Trigger>
                            <Select.Content>
                              {years.map(year => (
                                <Select.Item key={year.value} value={year.value}>
                                  {year.label}
                                </Select.Item>
                              ))}
                            </Select.Content>
                          </Select>
                        </Field.Control>
                      </Field>
                    </Box>
                    <Text color="text.weak" variant="text25">
                      {majorPurchaseOption.defaultTargetDateExplanation}
                    </Text>
                  </VStack>
                </Box>

                <Button type="submit" css={{ width: 'fit-content' }}>
                  Continue
                </Button>
              </VStack>
            </Form>
          </Formik>
        </VStack>
      </WorkflowLayout.Content>
      <WorkflowLayout.Footer>
        <Footer />
      </WorkflowLayout.Footer>
    </WorkflowLayout>
  );
};
